import { CircularProgress, Stack, Typography } from '@healthinal/ui';
import { createFileRoute } from '@tanstack/react-router';
import { useAtom } from 'jotai';
import { Suspense, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { GetPatientsBasicsType } from '../../api/generated/model';
import { useGetPatientsBasics } from '../../api/generated/patient-controller/patient-controller.ts';
import { ContentSection } from '../../components/ContentSection.tsx';
import { ErrorBoundary } from '../../components/errors/ErrorBoundary.tsx';
import { MotionDivider } from '../../components/MotionDivider.tsx';
import { StickyContainer } from '../../components/StickyContainer.tsx';
import { taskPatientSearchTextAtom } from '../../state/taskPatientSearchAtoms.ts';
import {
  taskAssignedEmployeeIdAtom,
  taskDueDateFilterAtom,
  taskIncludeCompletedAtom,
  taskSelectedPatientFilterAtom,
  taskSortDirAtom,
} from '../../state/taskSearchAtoms.ts';
import { formatDateYear } from '../../utils/date.ts';
import { PatientSideListNav } from '../components/PatientSideListNav.tsx';
import { TaskHeaderFilter } from './components/TaskHeaderFilter.tsx';
import { TaskList } from './components/TaskList.tsx';

export const Route = createFileRoute('/todos/')({
  component: Todos,
});

function Todos() {
  const [patientSearchText, setPatientSearchText] = useAtom(taskPatientSearchTextAtom);
  const [selectedPatient, setSelectedPatient] = useAtom(taskSelectedPatientFilterAtom);
  const [includeCompleted] = useAtom(taskIncludeCompletedAtom);
  const [dueDate] = useAtom(taskDueDateFilterAtom);
  const [assignedEmployeeId] = useAtom(taskAssignedEmployeeIdAtom);
  const [sortDir] = useAtom(taskSortDirAtom);
  const [debouncedPatientSearchText, setDebouncedPatientSearchText] = useState(patientSearchText);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedPatientSearchText(patientSearchText), 300);
    return () => window.clearTimeout(timeout);
  }, [patientSearchText]);

  const { data: patientsPage } = useGetPatientsBasics({
    type: GetPatientsBasicsType.ALL,
    assignedTo: 'ALL',
    page: 0,
    size: 20,
    search: debouncedPatientSearchText.trim() || undefined,
  });
  const patients = patientsPage?.content ?? [];

  const contentSectionRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (contentSectionRef.current) {
      contentSectionRef.current.scrollTop = 0;
    }
  }, [assignedEmployeeId, dueDate, includeCompleted, selectedPatient?.id, sortDir]);

  return (
    <Stack direction={'row'} gap={3}>
      <PatientSideListNav
        patients={patients}
        searchText={patientSearchText}
        setSearchText={setPatientSearchText}
        selectedPatient={selectedPatient}
        setSelectedPatient={setSelectedPatient}
        useClientSideSearch={false}
      />
      <ContentSection flex={4} ref={contentSectionRef}>
        <StickyContainer gap={2}>
          {selectedPatient ? (
            <Typography
              level={'h3'}
              endDecorator={
                <Typography fontWeight={400} textColor={'text.tertiary'}>
                  {formatDateYear(selectedPatient.birthDate)}
                </Typography>
              }>
              {selectedPatient.caseNumber} {selectedPatient.firstName}
            </Typography>
          ) : (
            <Typography level={'h3'}>Alle Patienten</Typography>
          )}
          <Typography level={'h4'}>ToDos</Typography>
          <TaskHeaderFilter />
          <MotionDivider contentSectionRef={contentSectionRef} />
        </StickyContainer>
        <ErrorBoundary>
          <Suspense
            fallback={
              <Stack alignItems={'center'} paddingTop={5}>
                <CircularProgress />
              </Stack>
            }>
            <TaskList contentSectionRef={contentSectionRef} />
          </Suspense>
        </ErrorBoundary>
      </ContentSection>
    </Stack>
  );
}
